import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Search, Trash2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminErrors() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const { data } = await api.get(`${V1}/admin/errors?days=${days}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    setData(data);
  };
  useEffect(() => { load(); }, [days]);

  const clearOld = async () => {
    if (!window.confirm("30 günden eski hata kayıtları silinsin mi?")) return;
    const { data: r } = await api.delete(`${V1}/admin/errors?days=30`);
    toast.success(`${r.deleted} kayıt silindi`);
    load();
  };

  const deleteOne = async (id) => {
    await api.delete(`${V1}/admin/errors/${id}`);
    toast.success("Silindi");
    load();
  };

  if (!data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="admin-errors-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">İzleme</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <AlertCircle className="h-7 w-7 text-destructive" /> Frontend Hata Ekranı
          </h1>
          <p className="text-muted-foreground text-sm mt-1">ErrorBoundary tarafından yakalanan ve platform&apos;a raporlanan tüm frontend hataları.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="h-9 px-2 border border-border rounded" data-testid="err-days">
            <option value="1">Son 1 gün</option>
            <option value="7">Son 7 gün</option>
            <option value="30">Son 30 gün</option>
            <option value="90">Son 90 gün</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="err-refresh"><RefreshCw className="h-4 w-4" /> Yenile</Button>
          <Button variant="outline" onClick={clearOld}><Trash2 className="h-4 w-4" /> Eski Kayıtları Temizle</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Metric label="Toplam Hata" value={data.total} />
        <Metric label="Ekranlanan" value={data.items.length} />
        <Metric label="Benzersiz İmza" value={data.top_signatures.length} accent />
      </div>

      {data.top_signatures.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="p-3 border-b border-border font-display font-bold">En Sık Görülen Hatalar</div>
          <div className="divide-y divide-border">
            {data.top_signatures.map((s, i) => (
              <div key={i} className="p-3 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground truncate">{s.path}</div>
                  <div className="text-sm truncate" data-testid={`sig-msg-${i}`}>{s.message}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-display font-bold">{s.count}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(s.last_at).toLocaleString("tr-TR")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Hata mesajı, path veya stack ara..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="err-search" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {data.items.length === 0 && <div className="p-10 text-center text-muted-foreground">Bu aralıkta hata yok — herşey yolunda gidiyor 🎉</div>}
        {data.items.map((e) => {
          const isOpen = expanded === e.id;
          return (
            <div key={e.id} className="border-b border-border last:border-b-0" data-testid={`err-row-${e.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : e.id)} className="w-full text-left p-3 hover:bg-muted/40 flex items-start gap-3">
                {isOpen ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span>{new Date(e.created_at).toLocaleString("tr-TR")}</span>
                    <span>·</span>
                    <span className="font-mono">{e.path}</span>
                    {e.user_id && <><span>·</span><span>uid={e.user_id.slice(0, 8)}</span></>}
                  </div>
                  <div className="text-sm mt-1 truncate">{(e.meta || {}).message || "(mesaj yok)"}</div>
                </div>
              </button>
              {isOpen && (
                <div className="p-4 pl-11 bg-muted/30 space-y-3 text-xs">
                  <div>
                    <div className="uppercase tracking-widest text-muted-foreground text-[10px] mb-1">Stack</div>
                    <pre className="whitespace-pre-wrap break-words bg-background p-2 rounded border border-border max-h-64 overflow-auto">{(e.meta || {}).stack || "(yok)"}</pre>
                  </div>
                  {(e.meta || {}).component && (
                    <div>
                      <div className="uppercase tracking-widest text-muted-foreground text-[10px] mb-1">Component Stack</div>
                      <pre className="whitespace-pre-wrap break-words bg-background p-2 rounded border border-border max-h-64 overflow-auto">{e.meta.component}</pre>
                    </div>
                  )}
                  {(e.meta || {}).user_agent && (
                    <div><span className="text-muted-foreground">User Agent:</span> <span className="font-mono">{e.meta.user_agent}</span></div>
                  )}
                  <div className="text-right">
                    <Button size="sm" variant="outline" onClick={() => deleteOne(e.id)} data-testid={`err-delete-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Sil
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Metric = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl border ${accent ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-3xl font-display font-bold mt-1">{value}</div>
  </div>
);
